// apps/accounts/src/components/ProtectedRoute.jsx
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import pb from '../lib/pocketbaseClient';

// ─── Route Permissions ──────────────────────────────────────────
const routePermissions = {
  // ─── Partner Routes (Always accessible to partners) ───
  '/partner/dashboard': ['partner-construction', 'partner-interior'],
  '/partner/my-sites': ['partner-construction', 'partner-interior'],
  '/partner/projects': ['partner-construction', 'partner-interior'],
  '/partner/projects/:id': ['partner-construction', 'partner-interior'],
  '/partner/partners': ['partner-construction', 'partner-interior'],
  '/partner/vendors': ['partner-construction', 'partner-interior'],
  '/partner/labour': ['partner-construction', 'partner-interior'],
  '/partner/attendance': ['partner-construction', 'partner-interior'],
  '/partner/expenses': ['partner-construction', 'partner-interior'],
  '/partner/vendor-payments': ['partner-construction', 'partner-interior'],
  '/partner/client-payments': ['partner-construction', 'partner-interior'],
  '/partner/materials': ['partner-construction', 'partner-interior'],
  '/partner/materials/requests': ['partner-construction', 'partner-interior'],
  '/partner/materials/purchase-orders': ['partner-construction', 'partner-interior'],

  // ─── Admin Dashboards ───
  '/dashboard/main': ['admin', 'accounts'],
  '/dashboard/construction': ['admin', 'accounts', 'partner-construction'],
  '/dashboard/interior': ['admin', 'accounts', 'partner-interior'],

  // ─── Partner Sites (Admin view) ───
  '/partner-sites': ['admin', 'accounts', 'partner-construction', 'partner-interior'],

  // ─── Core Modules (Admin only) ───
  '/projects': ['admin', 'accounts'],
  '/projects/:id': ['admin', 'accounts'],
  '/partners': ['admin', 'accounts'],
  '/vendors': ['admin', 'accounts', 'site_engineer', 'site_manager'],
  '/labour': ['admin', 'accounts', 'site_engineer', 'site_manager', 'site_supervisor'],
  '/attendance': ['admin', 'accounts', 'site_engineer', 'site_manager', 'site_supervisor'],

  // ─── Partner Management (Admin only) ───
  '/partner-site-management': ['admin', 'accounts'],
  '/site-employees': ['admin', 'accounts'],

  // ─── Expenses & Payments ───
  '/expenses': ['admin', 'accounts'],
  '/vendor-payments': ['admin', 'accounts'],
  '/client-payments': ['admin', 'accounts'],
  '/investments': ['admin', 'accounts'],
  '/reports': ['admin', 'accounts'],

  // ─── Materials Management ───
  '/materials': ['admin', 'accounts', 'site_engineer', 'site_supervisor', 'store_keeper'],
  '/materials/categories': ['admin', 'accounts'],
  '/materials/uoms': ['admin', 'accounts'],
  '/materials/suppliers': ['admin', 'accounts'],
  '/materials/requests': ['admin', 'site_engineer', 'site_supervisor', 'store_keeper'],
  '/materials/requests/:id': ['admin', 'site_engineer', 'site_supervisor', 'store_keeper'],
  '/materials/purchase-orders': ['admin', 'accounts', 'site_supervisor'],
  '/materials/purchase-orders/:id': ['admin', 'accounts', 'site_supervisor'],
};

// ─── Get Partner Tabs (using partner ID) ────────────────────────
const getPartnerTabs = async (partnerId) => {
  try {
    if (!partnerId) return [];
    
    const permissions = await pb.collection('partner_tab_permissions').getFullList({
      filter: `partner = "${partnerId}" && isActive = true`,
    });
    
    console.log(`📋 Found ${permissions.length} tab permissions for partner ${partnerId}`);
    return permissions.map(p => p.tabPath);
  } catch (error) {
    console.error('Error fetching partner tabs:', error);
    return [];
  }
};

// ─── Get Partner Record ──────────────────────────────────────────
const getPartnerRecord = async (userId, email) => {
  try {
    let filter = '';
    if (userId) {
      filter = `createdBy = "${userId}"`;
    }
    if (email && userId) {
      filter = `createdBy = "${userId}" || email = "${email}"`;
    } else if (email) {
      filter = `email = "${email}"`;
    }
    
    if (!filter) return null;
    
    const partners = await pb.collection('partners').getFullList({
      filter: filter,
    });
    
    if (partners.length > 0) {
      console.log(`✅ Found partner record: ${partners[0].id} - ${partners[0].name}`);
      return partners[0];
    }
    
    console.warn('⚠️ No partner record found for user');
    return null;
  } catch (error) {
    console.error('Error fetching partner record:', error);
    return null;
  }
};

// ─── Get Base Path ─────────────────────────────────────────────
const getBasePath = (path) => {
  if (!path) return null;
  const cleanPath = path.split('?')[0];
  
  // Check exact match
  if (routePermissions[cleanPath]) return cleanPath;
  
  // Check dynamic patterns
  const pathParts = cleanPath.split('/');
  const dynamicPatterns = Object.keys(routePermissions);
  
  for (const pattern of dynamicPatterns) {
    const patternParts = pattern.split('/');
    if (patternParts.length !== pathParts.length) continue;
    
    let match = true;
    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i].startsWith(':')) continue;
      if (patternParts[i] !== pathParts[i]) { 
        match = false; 
        break; 
      }
    }
    if (match) return pattern;
  }
  
  return cleanPath;
};

// ─── Get Partner Default Path ──────────────────────────────────
const getPartnerDefaultPath = (role) => {
  if (role === 'partner-construction') return '/partner/dashboard';
  if (role === 'partner-interior') return '/partner/dashboard';
  return '/partner/dashboard';
};

// ─── Main Component ────────────────────────────────────────────
export const ProtectedRoute = ({ children, path }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const currentPath = path || location.pathname;
  const role = user?.role;
  
  const [hasTabAccess, setHasTabAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [assignedTabs, setAssignedTabs] = useState([]);

  // ─── Loading State ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // ─── Not Authenticated ──────────────────────────────────────
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ─── Check Tab Access ──────────────────────────────────────
  useEffect(() => {
    const checkTabAccess = async () => {
      setCheckingAccess(true);
      setAssignedTabs([]);

      try {
        const isPartner = role === 'partner-construction' || role === 'partner-interior';

        // ─── For Partners ──────────────────────────────────────
        if (isPartner) {
          // ✅ STEP 1: Find partner record using user ID or email
          const partner = await getPartnerRecord(user.id, user.email);
          
          if (!partner) {
            console.warn('❌ No partner record found, allowing basic access');
            // Allow basic access even without partner record
            setHasTabAccess(true);
            setCheckingAccess(false);
            return;
          }

          // ✅ STEP 2: Get assigned tabs using partner.id (NOT user.id)
          const tabs = await getPartnerTabs(partner.id);
          setAssignedTabs(tabs);

          // ✅ STEP 3: Check if current path is accessible
          const basePath = getBasePath(currentPath);
          
          console.log(`🔍 Checking access for: ${currentPath}`);
          console.log(`📋 Base path: ${basePath}`);
          console.log(`📋 Assigned tabs:`, tabs);

          // ✅ ALLOW ALL /partner/* ROUTES - No tab permission needed
          if (currentPath.startsWith('/partner/')) {
            console.log('✅ Partner route - access granted');
            setHasTabAccess(true);
            setCheckingAccess(false);
            return;
          }

          // Check if path is in assigned tabs
          if (basePath && tabs.includes(basePath)) {
            console.log(`✅ Tab access granted: ${basePath}`);
            setHasTabAccess(true);
          } else {
            console.log(`❌ Tab access denied: ${basePath} not in [${tabs.join(', ')}]`);
            setHasTabAccess(false);
          }
        } else {
          // Admin/Accounts have full access
          console.log('✅ Admin/Accounts - Full access granted');
          setHasTabAccess(true);
        }
      } catch (error) {
        console.error('❌ Tab access check error:', error);
        // Allow access on error to prevent lockout
        setHasTabAccess(true);
      } finally {
        setCheckingAccess(false);
      }
    };

    checkTabAccess();
  }, [role, user, currentPath]);

  // ─── If checking access ──────────────────────────────────────
  if (checkingAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Verifying access...</p>
        </div>
      </div>
    );
  }

  // ─── If no tab access ──────────────────────────────────────────
  if (!hasTabAccess) {
    const isPartner = role === 'partner-construction' || role === 'partner-interior';
    
    // Redirect partner to their dashboard
    if (isPartner) {
      const defaultPath = getPartnerDefaultPath(role);
      console.log(`🔄 Redirecting partner to: ${defaultPath}`);
      return <Navigate to={defaultPath} replace />;
    }
    
    // Admin/Accounts redirect to main dashboard
    return (
      <div className="flex flex-col items-center justify-center h-64 p-6">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-xl font-bold text-slate-800">Access Denied</h2>
          <p className="text-slate-500 mt-2">
            You don't have permission to access this page.
            Please contact your administrator for tab access.
          </p>
          
          {/* Show assigned tabs if any */}
          {assignedTabs && assignedTabs.length > 0 && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Your assigned tabs:</p>
              <div className="flex flex-wrap gap-1 mt-1 justify-center">
                {assignedTabs.slice(0, 5).map((tab, idx) => (
                  <span key={idx} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                    {tab}
                  </span>
                ))}
                {assignedTabs.length > 5 && (
                  <span className="text-xs text-slate-400">+{assignedTabs.length - 5} more</span>
                )}
              </div>
            </div>
          )}
          
          <div className="mt-4 flex flex-wrap gap-3 justify-center">
            <button 
              onClick={() => window.location.href = '/partner/dashboard'}
              className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition"
            >
              Go to Partner Dashboard
            </button>
            <button 
              onClick={() => window.location.href = '/partner/my-sites'}
              className="px-6 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition"
            >
              View My Sites
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Check Role-based Permission ────────────────────────────
  const basePath = getBasePath(currentPath);
  const allowedRoles = basePath ? routePermissions[basePath] : null;

  // If no permission defined
  if (!allowedRoles) {
    const isPartner = role === 'partner-construction' || role === 'partner-interior';
    
    if (isPartner) {
      // Partner routes are always allowed
      if (currentPath.startsWith('/partner/')) {
        console.log('✅ Partner route - access granted');
        return children;
      }
      // Redirect to partner dashboard
      const defaultPath = getPartnerDefaultPath(role);
      console.log(`🔄 Redirecting partner to: ${defaultPath}`);
      return <Navigate to={defaultPath} replace />;
    }
    
    if (role === 'admin' || role === 'accounts') {
      return children;
    }
    
    return <Navigate to="/dashboard/main" replace />;
  }

  // Check if user's role is allowed
  if (!allowedRoles.includes(role)) {
    const isPartner = role === 'partner-construction' || role === 'partner-interior';
    
    if (isPartner) {
      // Partner routes are always allowed
      if (currentPath.startsWith('/partner/')) {
        console.log('✅ Partner route - access granted');
        return children;
      }
      // Redirect to partner dashboard
      const defaultPath = getPartnerDefaultPath(role);
      console.log(`🔄 Redirecting partner to: ${defaultPath}`);
      return <Navigate to={defaultPath} replace />;
    }
    
    console.log(`❌ Role ${role} not allowed for ${basePath}`);
    return <Navigate to="/dashboard/main" replace />;
  }

  // ─── Access Granted ─────────────────────────────────────────
  console.log(`✅ Access granted for ${currentPath}`);
  return children;
};

export default ProtectedRoute;